import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Calendar,
  AlertCircle,
  Eye
} from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription,
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { formatDateToBritish } from "@/lib/dateUtils";

interface PendingTermination {
  id: number;
  employee_id: number;
  employee_name: string;
  employer_name: string;
  termination_date: string;
  termination_reason: string;
  submitted_at: string;
  submitted_by: string;
  status: "pending_approval" | "approved" | "rejected";
  final_pay_amount?: number;
  notice_period_end?: string;
}

interface TerminationDetailsResponse {
  termination: PendingTermination;
  employee_details: {
    first_name: string;
    last_name: string;
    email: string;
    start_date: string;
  };
  final_pay_components: Array<{
    name: string;
    amount: number;
    description: string;
  }>;
}

const TerminationApprovals: React.FC = () => {
  const [pendingTerminations, setPendingTerminations] = useState<PendingTermination[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState<number | null>(null);
  const [selectedTermination, setSelectedTermination] = useState<TerminationDetailsResponse | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const { toast } = useToast();

  const fetchPendingTerminations = async () => {
    try {
      setIsLoading(true);
      const response = await ApiService.request<{ terminations: PendingTermination[] }>(
        EndpointKeys.LIST_PENDING_TERMINATIONS
      );
      setPendingTerminations(response.terminations || []);
    } catch (error) {
      console.error("Error fetching pending terminations:", error);
      toast({
        title: "Error",
        description: "Failed to fetch pending terminations",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingTerminations();
  }, []);

  const handleApproveTermination = async (terminationId: number) => {
    try {
      setIsApproving(terminationId);
      
      const response = await ApiService.request<{
        success: boolean;
        message: string;
        termination_status: string;
        p45_generated?: boolean;
      }>(
        EndpointKeys.APPROVE_TERMINATION,
        {
          method: "POST",
          urlParams: { termination_id: terminationId },
          body: { generate_p45: true },
        }
      );

      if (response.success) {
        toast({
          title: "Termination Approved",
          description: `${response.message}${response.p45_generated ? " P45 generated." : ""}`,
        });
        
        // Refresh the list
        await fetchPendingTerminations();
      } else {
        throw new Error(response.message || "Failed to approve termination");
      }
    } catch (error: any) {
      console.error("Error approving termination:", error);
      toast({
        title: "Approval Failed",
        description: error.message || "Failed to approve termination",
        variant: "destructive",
      });
    } finally {
      setIsApproving(null);
    }
  };

  const handleViewDetails = async (termination: PendingTermination) => {
    try {
      // For now, we'll create a mock detailed view since the API might not have this endpoint yet
      const detailsResponse: TerminationDetailsResponse = {
        termination,
        employee_details: {
          first_name: termination.employee_name.split(' ')[0],
          last_name: termination.employee_name.split(' ')[1] || '',
          email: 'employee@example.com',
          start_date: '2023-01-01',
        },
        final_pay_components: [
          { name: 'Final Salary', amount: 2500, description: 'Final month salary' },
          { name: 'Holiday Pay', amount: 350, description: 'Accrued holiday pay' },
          { name: 'Notice Pay', amount: 1250, description: 'Payment in lieu of notice' },
        ],
      };
      
      setSelectedTermination(detailsResponse);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error("Error fetching termination details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch termination details",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending_approval':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-300">Pending Approval</Badge>;
      case 'approved':
        return <Badge variant="outline" className="text-green-600 border-green-300">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading pending terminations...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-orange-500" />
              Pending Termination Approvals
            </CardTitle>
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              {pendingTerminations.length} Pending
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {pendingTerminations.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="mx-auto h-12 w-12 text-green-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Pending Approvals</h3>
              <p className="text-gray-500">
                All terminations have been processed or there are no terminations awaiting approval.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Employer</TableHead>
                    <TableHead>Termination Date</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingTerminations.map((termination) => (
                    <TableRow key={termination.id}>
                      <TableCell className="font-medium">
                        {termination.employee_name}
                      </TableCell>
                      <TableCell>{termination.employer_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                          {formatDateToBritish(termination.termination_date)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {termination.termination_reason?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{formatDateToBritish(termination.submitted_at)}</div>
                          <div className="text-gray-500">by {termination.submitted_by}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(termination.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(termination)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Details
                          </Button>
                          {termination.status === 'pending_approval' && (
                            <Button
                              size="sm"
                              onClick={() => handleApproveTermination(termination.id)}
                              disabled={isApproving === termination.id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              {isApproving === termination.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  Approving...
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Termination Details Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Termination Details</DialogTitle>
            <DialogDescription>
              Review the termination details before approval
            </DialogDescription>
          </DialogHeader>
          {selectedTermination && (
            <div className="space-y-6">
              {/* Employee Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Employee Information</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Name:</strong> {selectedTermination.employee_details.first_name} {selectedTermination.employee_details.last_name}</div>
                    <div><strong>Email:</strong> {selectedTermination.employee_details.email}</div>
                    <div><strong>Start Date:</strong> {formatDateToBritish(selectedTermination.employee_details.start_date)}</div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Termination Information</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Termination Date:</strong> {formatDateToBritish(selectedTermination.termination.termination_date)}</div>
                    <div><strong>Reason:</strong> {selectedTermination.termination.termination_reason?.replace(/_/g, ' ')}</div>
                    <div><strong>Submitted:</strong> {formatDateToBritish(selectedTermination.termination.submitted_at)}</div>
                  </div>
                </div>
              </div>

              {/* Final Pay Components */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Final Pay Components</h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Component</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedTermination.final_pay_components.map((component, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{component.name}</TableCell>
                          <TableCell>{component.description}</TableCell>
                          <TableCell className="text-right">£{component.amount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-gray-50 font-medium">
                        <TableCell colSpan={2}>Total Final Pay</TableCell>
                        <TableCell className="text-right">
                          £{selectedTermination.final_pay_components.reduce((sum, component) => sum + component.amount, 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsDetailsModalOpen(false)}
                >
                  Close
                </Button>
                {selectedTermination.termination.status === 'pending_approval' && (
                  <Button
                    onClick={() => {
                      handleApproveTermination(selectedTermination.termination.id);
                      setIsDetailsModalOpen(false);
                    }}
                    disabled={isApproving === selectedTermination.termination.id}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isApproving === selectedTermination.termination.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve Termination
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TerminationApprovals; 